/**
 * Tauri 桌面端服务
 * 提供文件读写、对话框等原生功能
 */

// 检测是否在 Tauri 环境中运行
export const isTauri = (): boolean => {
  return typeof window !== 'undefined' && 
    ('__TAURI__' in window || '__TAURI_INTERNALS__' in window);
};

// 动态导入 Tauri API（仅在 Tauri 环境中可用）
const getTauriApis = async () => {
  if (!isTauri()) {
    throw new Error('Not running in Tauri environment');
  }
  const { invoke } = await import('@tauri-apps/api/core');
  const { open, save } = await import('@tauri-apps/plugin-dialog');
  return { invoke, open, save };
};

/**
 * 打开文件选择对话框
 */
export const openFileDialog = async (): Promise<string | null> => {
  const { open } = await getTauriApis();
  const result = await open({
    multiple: false,
    filters: [
      {
        name: 'Markdown',
        extensions: ['md', 'markdown', 'mdown', 'mkd'],
      },
      {
        name: 'All Files',
        extensions: ['*'],
      },
    ],
  });
  return result as string | null;
};

/**
 * 打开保存文件对话框
 */
export const saveFileDialog = async (defaultName?: string): Promise<string | null> => {
  const { save } = await getTauriApis();
  const result = await save({
    defaultPath: defaultName,
    filters: [
      {
        name: 'Markdown',
        extensions: ['md'],
      },
    ],
  });
  return result;
};

/**
 * 读取 Markdown 文件
 */
export const readMarkdownFile = async (path: string): Promise<string> => {
  const { invoke } = await getTauriApis();
  return invoke<string>('read_markdown_file', { path });
};

/**
 * 写入 Markdown 文件
 */
export const writeMarkdownFile = async (path: string, content: string): Promise<void> => {
  const { invoke } = await getTauriApis();
  return invoke('write_markdown_file', { path, content });
};

/**
 * 打开并读取 Markdown 文件
 */
export const openAndReadFile = async (): Promise<{ path: string; content: string } | null> => {
  const path = await openFileDialog();
  if (!path) return null;
  
  const content = await readMarkdownFile(path);
  return { path, content };
};
